import { IconFont } from '@/components';
import { getBuiltinCharsConfig, updateBuiltinChars } from '@/services/compare';
import { cloneDeep } from '@/utils';
import { createSharedState } from '@ai-indeed/hooks';
import { Alert, Button, Checkbox } from '@ai-indeed/idp-ui';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { App, Modal, Tooltip } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useState } from 'react';
import { useCheckedList } from '../IgnorePunctuation';
import './index.less';

interface CharacterRecord {
  value: string;
  checked: boolean;
}

export const useCharacterSet = createSharedState<CharacterRecord[]>([]);
const CustomCharacterItem = (props: {
  defaultValue: CharacterRecord;
  message: MessageInstance;
}) => {
  const { defaultValue, message } = props;
  const [characterSet, setCharacterSet] = useCharacterSet();
  const [newValue, setNewValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isUnfold, setIsUnfold] = useState(false);
  const handleCharacterChange = (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const oldOrNewValue = newValue || defaultValue.value;
    const currentValue = e.currentTarget.value.trim();
    const idx = characterSet.findIndex((char) => char.value === oldOrNewValue);
    if (currentValue.length > 1024) {
      message.error('自定义字符长度不可超过1024个字符');
      return;
    }
    setIsEditing(false);
    setNewValue(currentValue);
    setCharacterSet(
      characterSet.map((char, index) =>
        index === idx
          ? {
              value: currentValue,
              checked: true,
            }
          : char,
      ),
    );
  };
  const value = newValue ? newValue : defaultValue.value;
  return (
    <div className="flex items-center cursor-pointer relative">
      {isEditing ? (
        <input
          className="addInput"
          defaultValue={newValue || defaultValue.value}
          autoFocus
          onBlur={(e) => handleCharacterChange(e)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCharacterChange(e);
          }}
        />
      ) : (
        <div
          className={classNames('h-9 min-w-[94px] px-3 rounded group', {
            'bg-[#EDF1FE] hover:bg-[#E2E7FD]': defaultValue.checked,
          })}
          onBlur={() => {
            setIsEditing(false);
            setTimeout(() => {
              setIsUnfold(false);
            });
          }}
          onDoubleClick={() => {
            setIsEditing(true);
          }}
          onMouseEnter={() => {
            setIsUnfold(true);
          }}
          onMouseLeave={() => {
            setIsUnfold(false);
          }}
          onClick={() => {
            const newestSet = characterSet.map((char) => {
              if (char.value === defaultValue.value) {
                return {
                  value: char.value,
                  checked: !char.checked,
                };
              }
              return char;
            });
            setCharacterSet(newestSet);
          }}
        >
          <Tooltip title={value.length > 22 ? value : ''}>
            <div className="inline-flex items-center w-full h-full relative">
              <p className="w-full text-primary text-sm text-center">
                {value.length > 22 ? value.slice(0, 22) + '...' : value}
              </p>
              <button
                type="button"
                className="absolute top-0 -right-1 pl-2.5 h-9 text-base text-primary invisible group-hover:visible"
                style={
                  defaultValue.checked
                    ? {
                        background:
                          'linear-gradient(90deg, rgba(226, 231, 253 ,0.3) 0%, rgba(226, 231, 253, 0.9) 33%,#E2E7FD 100%)',
                      }
                    : {
                        background:
                          'linear-gradient(90deg, rgba(255, 255, 255 ,0.3) 0%, rgba(255, 255, 255, 0.9) 33%,#fff 100%)',
                      }
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                  setIsUnfold(false);
                }}
              >
                <IconFont type="icon-bianji2" />
              </button>
            </div>
          </Tooltip>

          {isUnfold && (
            <button
              type="button"
              className="absolute -top-2 -right-2 text-base"
              onClick={(e) => {
                e.stopPropagation();
                const idx = characterSet.findIndex(
                  (char) => char.value === defaultValue.value,
                );
                const tmp = cloneDeep(characterSet);
                tmp.splice(idx, 1);
                setCharacterSet(tmp);
                setNewValue('');
              }}
            >
              <IconFont type="icon-shibai" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
const CustomCharacterWrap = (props: { message: MessageInstance }) => {
  const { message } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [characterSet, setCharacterSet] = useCharacterSet();

  useEffect(() => {
    getBuiltinCharsConfig().then((res) => {
      const customizedCharaters =
        res.data.regulation.char_filter_out.customized;
      const initialCharacter = customizedCharaters.map((char: string) => {
        return {
          value: char,
          checked: true,
        } as CharacterRecord;
      });
      setCharacterSet(uniqBy([...characterSet, ...initialCharacter], 'value'));
    });
  }, []);
  return (
    <div className="px-6 flex gap-1">
      <section className="flex flex-wrap gap-4">
        {isEditing ? (
          <input
            className="addInput"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = (e.target as HTMLInputElement).value.trim();
                if (value.length > 1024) {
                  message.error('自定义字符长度不可超过1024个字符');
                  return;
                }
                if (characterSet.some((char) => char.value === value)) return;
                if (value) {
                  setCharacterSet([
                    ...characterSet,
                    {
                      value,
                      checked: true,
                    },
                  ]);
                }
                setIsEditing(!isEditing);
              }
            }}
            onBlur={(e) => {
              const value = (e.target as HTMLInputElement).value.trim();
              if (value.length > 1024) {
                message.error('自定义字符长度不可超过1024个字符');
                return;
              }
              if (characterSet.some((char) => char.value === value)) {
                setIsEditing(!isEditing);
                return;
              }
              if (value) {
                setCharacterSet([
                  ...characterSet,
                  {
                    value,
                    checked: true,
                  },
                ]);
              }
              setIsEditing(!isEditing);
            }}
          />
        ) : (
          <button
            type="button"
            className="addCharacter"
            onClick={() => {
              setIsEditing(!isEditing);
            }}
          >
            <IconFont type="icon-tianjia1" className="text-sm mr-1" />
            添加字符
          </button>
        )}
        {characterSet
          .map((char, index) => {
            return (
              <CustomCharacterItem
                key={'ignorecustomchar' + index}
                defaultValue={char}
                message={message}
              />
            );
          })
          .reverse()}
      </section>
    </div>
  );
};

export default NiceModal.create(() => {
  const { useApp } = App;
  const { message } = useApp();
  const { remove } = useModal();
  const [characterSet, setCharacterSet] = useCharacterSet();
  const [checkedList] = useCheckedList();
  const checkAll = characterSet.every((char) => char.checked);
  const indeterminate =
    !characterSet.every((char) => !char.checked) &&
    characterSet.some((char) => !char.checked);

  const onCheckAllChange = () => {
    if (!checkAll)
      setCharacterSet(
        Array.from(
          characterSet.map((char) => {
            return {
              value: char.value,
              checked: true,
            };
          }),
        ),
      );
    else {
      setCharacterSet(
        characterSet.map((char) => {
          return {
            value: char.value,
            checked: false,
          };
        }),
      );
    }
  };
  return (
    <Modal
      width={968}
      centered
      className="modal-reset"
      open={true}
      onCancel={remove}
      title={
        <div className="flex gap-1 justify-center">
          <div>忽略自定义字符</div>
          <Tooltip
            overlayInnerStyle={{ width: 260 }}
            arrow={true}
            title="选中的自定义字符，将不作为比对中的差异点"
          >
            <IconFont type="icon-bangzhu2" className="text-icon-3"></IconFont>
          </Tooltip>
        </div>
      }
      footer={
        <>
          <Button
            type="outline1"
            size="large"
            className="w-[86px]"
            onClick={remove}
          >
            取消
          </Button>
          <Button
            type="primary"
            size="large"
            className="w-[86px]"
            onClick={() => {
              updateBuiltinChars({
                char_filter_out: {
                  builtin: checkedList
                    .filter((record) => record.checked)
                    .map((res) => res.value),
                  customized: characterSet
                    .filter((record) => record.checked)
                    .map((res) => res.value),
                  base_customized: characterSet.map((v) => v.value),
                },
                filter_type: 'CUSTOMIZED',
              }).then(() => {
                message.success('保存成功');
                remove();
              });
            }}
          >
            确定
          </Button>
        </>
      }
    >
      <div className="relative w-full max-h-[578px] pb-8 overflow-auto ">
        <Alert
          message="本次修改将应用于当前及后续任务中"
          type="info"
          closable
          showIcon
          className="sticky top-0 rounded-none"
        />
        <div className="flex justify-between items-center ml-6 mr-[14px] my-4">
          <h3>自定义字符</h3>
          <Checkbox
            checked={checkAll}
            indeterminate={indeterminate}
            onChange={onCheckAllChange}
          >
            全选
          </Checkbox>
        </div>
        <CustomCharacterWrap message={message} />
      </div>
    </Modal>
  );
});
